import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import PropTypes from "prop-types";

const NavigationListItem = ({
  title,
  href,
  depth,
  children,
  pageid,
  menuIcon,
  hasActiveChild,
  hasChildren,
  openMenuId, // new prop (currently open top-level menu id)
  setOpenMenuId, // new prop setter
}) => {
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        if (isOpen) {
          setIsOpen(false);
          if (depth === 0) {
            setOpenMenuId(null);
          }
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, depth, setOpenMenuId]);

  const isCurrent =
    href && (location.pathname === `/${href}` || location.pathname === href);

  const isActive = isCurrent || hasActiveChild;

  //Auto-close this menu when another top-level menu is opened
  useEffect(() => {
    if (depth === 0 && openMenuId !== pageid) {
      setIsOpen(false);
    }
  }, [openMenuId, depth, pageid]);

  // ---- Top-level standalone page (depth 0, no children) ----
  if (depth === 0 && !hasChildren) {
    const parentClass = isActive ? "activeTab-sideNav" : "InactiveTab-sideNav";

    return (
      <div
        className="tooltip-wrapper"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Link
          to={href ? `/${href.replace(/^\//, "")}` : "#"}
          onClick={() => {
            setIsHovered(false);
            setOpenMenuId(null);
          }}
        >
          <button
            className={`mb-2 text-decoration-none parent-nav-btn ${parentClass}`}
            style={{ textAlign: "center" }}
          >
            {menuIcon && <img src={menuIcon} alt="icon" className="nav-icon" />}
          </button>
        </Link>

        {isHovered && <div className="custom-tooltip">{title}</div>}
      </div>
    );
  }

  // ---- Top-level parent (depth 0, with children) ----
  if (depth === 0) {
    const parentClass =
      isOpen || hasActiveChild ? "activeTab-sideNav" : "InactiveTab-sideNav";

    const toggleOpen = () => {
      const newState = !isOpen;
      setIsOpen(newState);
      setIsHovered(false);
      //  Notify parent which top-level menu is open
      if (newState) setOpenMenuId(pageid);
    };

    return (
      <div
        ref={wrapperRef}
        className="nav-item-with-children tooltip-wrapper"
        onMouseEnter={() => depth === 0 && setIsHovered(true)}
        onMouseLeave={() => depth === 0 && setIsHovered(false)}
      >
        <button
          onClick={toggleOpen}
          className={`mb-2 text-decoration-none parent-nav-btn ${parentClass}`}
          style={{ textAlign: "center" }}
        >
          {menuIcon && <img src={menuIcon} alt="icon" className="nav-icon" />}
        </button>

        {depth === 0 && isHovered && (
          <div className="custom-tooltip">{title}</div>
        )}

        {isOpen && (
          <div className="submenu-dropdown submenu-right">
            {React.Children.map(children, (child) =>
              React.cloneElement(child, {
                openMenuId,
                setOpenMenuId,
              })
            )}
          </div>
        )}
      </div>
    );
  }

  // ---- Submenu item (depth > 0) ----
  if (hasChildren) {
    const toggleSubmenu = () => setIsOpen((prev) => !prev);

    return (
      <div className="tooltip-wrapper" ref={wrapperRef}>
        <button
          onClick={toggleSubmenu}
          className={`mb-2 text-decoration-none submenu-item ${
            isActive ? "activeTab-sideNav" : ""
          }`}
          style={{ textAlign: "center" }}
        >
          {menuIcon && <img src={menuIcon} alt="icon" className="nav-icon" />}
          {depth > 0 && <span className="submenu-text">{title}</span>}
          {/* expand icon for nested parent in child */}
          <span
            className="submenu-expand-icon"
            onClick={toggleSubmenu}
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
            aria-hidden="true"
          >
            <img src="/assets/images/arrow-next.svg" alt="open" />
          </span>
        </button>

        {isOpen && (
          <div className="submenu-dropdown submenu-right">
            {React.Children.map(children, (child) =>
              React.cloneElement(child, {
                openMenuId,
                setOpenMenuId,
              })
            )}
          </div>
        )}
      </div>
    );
  }

  // ---- Leaf menu ----
  return (
    <div className="tooltip-wrapper">
      <Link
        to={href ? `/${href.replace(/^\//, "")}` : "#"}
        onClick={() => {
          setIsHovered(false);
          setOpenMenuId(null); // ✅ close all menus when navigating
        }}
      >
        <button
          className={`mb-2 text-decoration-none submenu-item ${
            isActive ? "activeTab-sideNav" : ""
          }`}
          style={{ textAlign: "center" }}
        >
          {menuIcon && <img src={menuIcon} alt="icon" className="nav-icon" />}
          {depth > 0 && <span className="submenu-text">{title}</span>}
        </button>
      </Link>
    </div>
  );
};

NavigationListItem.propTypes = {
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  title: PropTypes.string.isRequired,
  pageid: PropTypes.string,
  menuIcon: PropTypes.string,
  hasActiveChild: PropTypes.bool,
  hasChildren: PropTypes.bool,
  openMenuId: PropTypes.string,
  setOpenMenuId: PropTypes.func,
};

NavigationListItem.defaultProps = {
  depth: 0,
  hasActiveChild: false,
};

export default NavigationListItem;
